import { useRef } from "react";
import { useForm } from "react-hook-form";

import { DateFilter } from "components/form_components/Inputs";
import { useSignalSettings } from "lib/hooks/api/teams/useSignalSettings";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "../../lib/featureFlags";
import { useSubscription } from "../../lib/hooks/subscription";
import { useFormAutoSubmit } from "../../lib/hooks/useFormAutoSubmit";
import { camelToSentence, isObjEmpty } from "../../lib/utils";
import FilterCollapse from "../filter_form/filter_collapse/FilterCollapse";
import FilterCollapseHeader from "../filter_form/filter_collapse_header/FilterCollapseHeader";
import FilterFormTitle from "../filter_form/filter_form_title/FilterFormTitle";
import { countFilters } from "../filter_form/utils";
import { frameworkFilterContents } from "./filter_groups";
import { useFrameworksTracking } from "./tracking";
import { DEFAULT_FILTERS, FrameworkFilters } from "./utils";

export const groupedKeys: Record<FilterGroupKeys, (keyof FrameworkFilters)[]> = {
  keywords: ["keywords", "excludeKeywords"],
  procurementStage: ["stage"],
  framework: ["procedureType", "awardType"],
  contractValue: ["value"],
  contractDates: ["closeDate", "startDate", "endDate"],
  signalScores: ["signalScore"],
  buyers: ["buyerIds", "buyerListIds", "buyerCategories", "buyerCountryRegions"],
  suppliers: ["supplierIds"],
  cpvCode: ["cpvDimensions", "cpvCodes"],
};

export type FilterSections = "suppliers" | "buyers";

export type FilterFormProps = {
  onChange: (filters: FrameworkFilters) => void;
  filters: FrameworkFilters;
  hiddenSections?: FilterSections[];
  onClose: () => void;
  activeGroups: string[];
  onActiveGroupsChange: (group: string[]) => void;
};

type FilterGroupKeys = keyof typeof frameworkFilterContents;

export function FilterForm({
  onChange,
  filters,
  hiddenSections = [],
  onClose,
  activeGroups,
  onActiveGroupsChange,
}: FilterFormProps) {
  const {
    control,
    watch,
    getValues,
    trigger,
    setValue,
    reset,
    formState: { touchedFields },
  } = useForm<FrameworkFilters>({
    defaultValues: filters,
  });
  const previousFilters = useRef(filters);

  const { data: signalSettings } = useSignalSettings();
  const tracking = useFrameworksTracking();

  const subscription = useSubscription();
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: (data) => {
      tracking.filterFormChanged(previousFilters.current, data, signalSettings);
      previousFilters.current = data;
      onChange(data);
    },
  });

  const data = watch();

  function hideNullsWhenSet(fieldName: "closeDate" | "startDate" | "endDate", value: DateFilter) {
    // don't set when clearing field
    const hasTransitionedToTruthy = Object.keys(value).some((field) => {
      const prevFieldValue = previousFilters.current?.[fieldName]?.filter;
      return isObjEmpty(prevFieldValue) && value[field];
    });

    // don't set if checkbox has been changed manually already
    if (hasTransitionedToTruthy && !touchedFields[fieldName]?.hideNulls) {
      setValue(`${fieldName}.hideNulls`, true);
    }
  }

  const counts = countFilters(filters, groupedKeys);

  function clearFilter(key: FilterGroupKeys) {
    const keys = groupedKeys[key];
    keys.forEach((k) => {
      setValue(k, DEFAULT_FILTERS[k]);
    });
  }

  const filterProps = {
    control,
    data,
    hideNullsWhenSet,
    isBuyerIdsFilterApplied: !!filters.buyerIds,
  };

  function getFilterGroups() {
    let items = (Object.keys(groupedKeys) as FilterGroupKeys[]).map((key) => {
      const ContentComponent = frameworkFilterContents[key];

      return {
        key,
        header: (
          <FilterCollapseHeader
            title={key === "cpvCode" ? "CPV code" : camelToSentence(key)}
            count={counts[key]}
            clear={() => clearFilter(key)}
            paywallIcon={key === "suppliers" && !subscription.hasDataTypes("SUPPLIERS")}
          />
        ),
        content: <ContentComponent {...filterProps} />,
      };
    });

    if (hiddenSections.includes("suppliers"))
      items = items.filter((item) => item.key !== "suppliers");

    if (hiddenSections.includes("buyers")) items = items.filter((item) => item.key !== "buyers");

    items = isRelevancySearchEnabled ? items.filter((item) => item.key !== "signalScores") : items;

    return items;
  }

  const items = getFilterGroups();

  function clearAll() {
    reset({ ...DEFAULT_FILTERS, searchText: filters.searchText || "" });
  }

  return (
    <form>
      <FilterFormTitle
        clearFilters={clearAll}
        showClear={Object.values(counts).some(Boolean)}
        onClose={onClose}
      />
      <FilterCollapse items={items} value={activeGroups} onChange={onActiveGroupsChange} />
    </form>
  );
}
